"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { ShieldCheck, Download, Copy, Check, AlertTriangle } from "lucide-react";
import { QRCodeSVG } from "@rc-component/qrcode";
import { Button, Input, Label } from "../ui";

/**
 * Die Zwei-Faktor-Anmeldung als KARTE — nicht als Reiter.
 *
 * **Warum sie ZweitfaktorKarte heisst und nicht mehr SicherheitTab** (0.327.0):
 * sie war nie ein Reiter, sondern immer ein Block, den andere einbetten. Der Name
 * „SicherheitTab" gehoert dem Reiter, der sie jetzt traegt — und zwei Dinge
 * gleichen Namens nebeneinander sind die Stelle, an der jemand das falsche
 * importiert.
 *
 * ── WO SIE GERENDERT WIRD, UND WARUM NICHT DIE HUELLE SIE ANHAENGT ──────────
 *
 * Getragen wird sie vom System-Reiter „Sicherheit" (`SicherheitTab`), zusammen mit
 * E-Mail- und Passwortwechsel. Bis 0.268.0 haengte `SettingsPage` einen Reiter
 * „Sicherheit" VON SICH AUS an; das erreichte zwar jede App, aber die Reiter-Huelle
 * baut nicht nur Kontoseiten: in `janeway-ai-chat` baut sie auch „Arbeitsbereich",
 * und dort stand die Kontosicherheit zwischen Widgets und Skills. Von 0.269.0 bis
 * 0.326.0 hing sie deshalb im Profil-Reiter.
 *
 * Seit 0.327.0 traegt sie ein eigener Reiter — aber als FABRIK
 * (`sicherheitSettingsTab()`), die jede App ausdruecklich in ihre Reiterliste
 * setzt. Damit kann er nicht mehr auf einer Seite auftauchen, die keine Kontoseite
 * ist: die Huelle haengt nichts von sich aus an, der Aufrufer entscheidet.
 * Waechter: `src/__tests__/zweitfaktor-steht-im-sicherheitsreiter.test.ts`.
 *
 * Direkt gerendert wird sie ausserdem an zwei Stellen ohne Reiterleiste:
 * `AuthOnboarding` (Pflicht-Einrichtung, `erzwungen`) und `janeway-auth`s eigener
 * Seite `/settings/security`.
 *
 * ## Was hier direkt mit Supabase spricht und was ueber die eigene App geht
 *
 * Einrichten und Pruefen des Codes gehen **direkt an GoTrue** (`supabase.auth.mfa.*`) —
 * das laeuft von jeder Herkunft aus, weil es der Supabase-Ursprung selbst ist. Der
 * Client wird nachgeladen statt hereingereicht, genau wie in `AuthOnboarding`: sonst
 * muesste ihn jede der 20 Apps durchreichen, und der Reiter waere wieder etwas, das
 * man vergessen kann.
 *
 * Der Wiederherstellungsschluessel geht ueber `/api/v1/zweitfaktor` der **eigenen** App.
 * Sie brauchen den Dienstschluessel, und ein Aufruf zur Auth-App waere ein
 * Fremdursprung mit Sitzungskeks — den laesst `corsHeaders` bewusst nicht zu
 * (Security-Audit 2026-07-14, R2). Fehlt die Route in einer App, sagt dieser Reiter
 * das ausdruecklich, statt einen leeren Stand zu zeigen.
 *
 * ## Der Ablauf, und warum er diese Reihenfolge hat
 *
 *   1. QR-Code scannen, sechsstelligen Code eingeben  → Faktor ist verifiziert
 *   2. EIN Wiederherstellungsschluessel, herunterladen oder kopieren
 *   3. **Ihn vollstaendig zurueckgeben**
 *
 * Schritt 3 ist kein Zierrat: wer den GANZEN Schluessel eintippt, belegt damit, dass er
 * ihn hat. Bis 2026-08-20 waren es zehn Codes und die Rueckfrage nach einem davon — das
 * belegte weniger und dauerte laenger. Die zehn kauften hier ohnehin nichts: ein
 * eingeloester Code entfernt den FAKTOR, danach wird neu eingerichtet und der ganze Satz
 * ersetzt. Die uebrigen neun konnten nie zum Einsatz kommen.
 */

interface Faktor {
  id: string;
  status: string;
  friendly_name?: string;
}

interface Stand {
  vorhanden: boolean;
  erzeugt_am: string | null;
  bestaetigt_am: string | null;
}

export interface ZweitfaktorKarteProps {
  /** Basispfad der eigenen App, falls sie unter einem Unterpfad laeuft. */
  basePath?: string;
  /**
   * Pflicht-Einrichtung: kein Einstieg, kein Zurueck, kein Abbrechen — die
   * Einrichtung beginnt von selbst und der einzige Weg heraus ist, sie zu Ende zu
   * bringen (oder sich abzumelden, das entscheidet die Seite drumherum).
   *
   * Der erklaerende Vorspann und die Statuskarte entfallen: auf der Pflichtseite
   * steht beides schon in der Ueberschrift, und ein Kasten „Zwei-Faktor-Anmeldung
   * ist aus" neben einem Knopf „Einrichten" liest sich wie eine Wahl.
   */
  erzwungen?: boolean;
  /** Wird gerufen, sobald Faktor UND Wiederherstellungsschluessel bestaetigt sind. */
  onFertig?: () => void;
  /** Wohin es danach geht — steht auf dem Knopf („Weiter zu AI Chat"). */
  zielName?: string;
  /**
   * Ohne die Lesebreite von `max-w-xl` — fuer den Sicherheits-Reiter.
   *
   * Der Vorgabewert ist die schmale Fassung, und das bleibt richtig, wo die Karte
   * ALLEIN auf der Seite steht (`AuthOnboarding`, `/settings/security`): eine
   * Erklaerung ueber 1200 px liest sich schlecht. Im Reiter steht sie dagegen
   * unter zwei Bloecken, die die volle Breite nehmen, und blieb dort als einzige
   * auf halber Strecke stehen.
   */
  breit?: boolean;
}

/**
 * Der QR-Code wird SELBST erzeugt, nicht der von Supabase gerenderte uebernommen.
 *
 * Supabase liefert unter `totp.qr_code` ein fertiges SVG in Schwarz auf Weiss — nicht
 * einfaerbbar, nicht anpassbar, und ohne Platz fuer ein Zeichen in der Mitte. Aus
 * `totp.uri` (dieselbe `otpauth://`-Adresse) laesst sich derselbe Code hier erzeugen,
 * mit Fehlerkorrektur `H` und ausgespartem Feld fuer das Schluessel-Zeichen.
 *
 * **Warum `H` und `excavate` zusammengehoeren:** ein Zeichen in der Mitte verdeckt
 * Module. Nur die hoechste Fehlerkorrektur traegt das (rund 30 % Verlust), und
 * `excavate` raeumt die verdeckten Module sauber weg, statt sie durchscheinen zu
 * lassen. Ohne beides entsteht ein huebscher Code, den kein Telefon liest.
 *
 * **Und warum er trotzdem dunkel auf hell bleibt:** ein invertierter QR-Code ist
 * nicht verlaesslich scanbar — viele Leser erwarten dunkle Module auf hellem Grund.
 * Auf dem Anmeldeweg ist das kein Ort fuer Geschmack. Angepasst wird deshalb der
 * RAHMEN: Karte, Rand und Glanz folgen dem Theme, die Modulflaeche nicht.
 */
const QR_VORDERGRUND = "#101014";
const QR_HINTERGRUND = "#ffffff";

/** Schluessel-Zeichen fuer die Mitte — Lucide `key-round`, als eigenstaendiges SVG. */
const QR_GLYPH =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" ` +
      `stroke="${QR_VORDERGRUND}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">` +
      `<path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z"/>` +
      `<circle cx="16.5" cy="7.5" r="1.25" fill="${QR_VORDERGRUND}" stroke="none"/>` +
      `</svg>`,
  );

/** Die drei Schritte der Einrichtung, in der Reihenfolge, in der sie laufen. */
const SCHRITTE = [
  { id: "einrichten", titel: "App verbinden", was: "QR-Code scannen" },
  { id: "codes", titel: "Schlüssel sichern", was: "Herunterladen oder kopieren" },
  { id: "bestaetigen", titel: "Bestätigen", was: "Schlüssel zurückgeben" },
] as const;

/**
 * Fortschritt der Einrichtung — drei Schritte, waagerecht, mit fuellender Linie.
 *
 * **Monochrom, wie der Rest der Suite** — `ThemeVars` kennt kein `success`. Der
 * erledigte Schritt traegt einen Haken, der laufende einen gefuellten Kreis, der
 * kommende einen leeren Umriss. Ein Zustand, der allein an Farbe haengt, ist im
 * Ausdruck und bei Farbenblindheit weg.
 *
 * Die Verbindungslinie fuellt sich mit dem Fortschritt. Sie traegt „wie weit" auch
 * fuer den, der die Zahlen nicht liest — und sie ist der Grund, warum die Schritte
 * waagerecht stehen statt als Liste.
 */
// Die Karten tragen KEINE eigenen Nummern mehr („1. QR-Code scannen"). Zwei
// Zaehlungen nebeneinander — „Schritt 1 von 3" oben, „1./2." in der Karte — lesen
// sich wie vier Schritte statt drei; am 2026-08-19 im Bild aufgefallen.
function Fortschritt({ aktiv }: { aktiv: string }) {
  const index = SCHRITTE.findIndex((s) => s.id === aktiv);
  if (index < 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Schritt {index + 1} von {SCHRITTE.length}
        </span>
        <span className="truncate text-[11px] text-muted-foreground/80">{SCHRITTE[index].was}</span>
      </div>

      <ol className="flex items-start" aria-label={`Schritt ${index + 1} von ${SCHRITTE.length}`}>
        {SCHRITTE.map((s, i) => {
          const erledigt = i < index;
          const laeuft = i === index;
          return (
            <li key={s.id} className="flex flex-1 items-start last:flex-none">
              <div className="flex w-20 shrink-0 flex-col items-center gap-2 text-center">
                <span
                  aria-current={laeuft ? "step" : undefined}
                  className={`flex h-8 w-8 items-center justify-center rounded-full border text-xs font-medium tabular-nums transition-all duration-300 ${
                    laeuft
                      ? "border-foreground bg-foreground text-background ring-4 ring-foreground/10"
                      : erledigt
                        ? "border-foreground/50 bg-foreground/10 text-foreground"
                        : "border-border text-muted-foreground/70"
                  }`}
                >
                  {erledigt ? <Check size={14} strokeWidth={2.5} /> : i + 1}
                </span>
                <span
                  className={`text-[11px] leading-tight ${
                    laeuft ? "font-medium text-foreground" : "text-muted-foreground"
                  }`}
                >
                  {s.titel}
                </span>
              </div>

              {i < SCHRITTE.length - 1 && (
                // `mt-4` setzt die Linie auf die Mitte des 2rem hohen Kreises. Ohne sie
                // haengt sie am oberen Rand und schneidet die Beschriftungen.
                <span className="mt-4 h-px flex-1 overflow-hidden rounded-full bg-border" aria-hidden>
                  <span
                    className="block h-full bg-foreground/50 transition-all duration-500"
                    style={{ width: erledigt ? "100%" : "0%" }}
                  />
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** Nur die Teile des Supabase-Clients, die dieser Reiter benutzt. */
interface MfaClient {
  auth: {
    mfa: {
      listFactors(): Promise<{
        data?: { totp?: Faktor[]; all?: (Faktor & { factor_type?: string })[] } | null;
        error?: { message: string } | null;
      }>;
      enroll(o: { factorType: "totp"; friendlyName?: string }): Promise<{
        data?: { id: string; totp?: { qr_code?: string; secret?: string; uri?: string } } | null;
        error?: { message: string } | null;
      }>;
      challenge(o: { factorId: string }): Promise<{ data?: { id: string } | null; error?: { message: string } | null }>;
      verify(o: { factorId: string; challengeId: string; code: string }): Promise<{ error?: { message: string } | null }>;
      unenroll(o: { factorId: string }): Promise<{ error?: { message: string } | null }>;
    };
  };
}

async function mfaClient(): Promise<MfaClient> {
  const { createSupabaseBrowser } = await import("@janeway/app-sdk/supabase");
  return createSupabaseBrowser() as unknown as MfaClient;
}

type Schritt = "uebersicht" | "einrichten" | "codes" | "bestaetigen" | "fertig";

export function ZweitfaktorKarte({ basePath = "", erzwungen = false, onFertig, zielName, breit = false }: ZweitfaktorKarteProps = {}) {
  const [faktoren, setFaktoren] = useState<Faktor[] | null>(null);
  const [stand, setStand] = useState<Stand | null | "fehlt">(null);
  const [schritt, setSchritt] = useState<Schritt>("uebersicht");
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  const [enroll, setEnroll] = useState<{ id: string; uri?: string; secret?: string } | null>(null);
  const [code, setCode] = useState("");
  const [neuerSchluessel, setNeuerSchluessel] = useState<string | null>(null);
  const [datei, setDatei] = useState<string>("");
  const [bestaetigung, setBestaetigung] = useState("");
  const [kopiert, setKopiert] = useState(false);
  /**
   * Restsekunden bis zur automatischen Weiterleitung. `null` = kein Zaehler laeuft.
   *
   * Der Zaehler ist SICHTBAR und abbrechbar: eine Seite, die nach unbestimmter Zeit
   * von selbst wegspringt, fuehlt sich an wie ein Fehler — genau das war die Klage
   * ueber den ersten Durchlauf.
   */
  const [restSekunden, setRestSekunden] = useState<number | null>(null);

  const api = `${basePath}/api/v1/zweitfaktor`;
  const aktiv = (faktoren ?? []).some((f) => f.status === "verified");

  const ladeStand = useCallback(async () => {
    const { data } = await (await mfaClient()).auth.mfa.listFactors();
    setFaktoren(data?.totp ?? []);
    try {
      const res = await fetch(api);
      // 404 heisst: diese App hat die Durchreiche nicht. Das ist ein Defekt und muss
      // als solcher dastehen — die stille leere Liste war genau der Fehler, den die
      // Workspace-Routen schon einmal hatten.
      if (res.status === 404) { setStand("fehlt"); return; }
      const j = (await res.json()) as { data?: Stand };
      setStand(j.data ?? null);
    } catch {
      setStand(null);
    }
  }, [api]);

  useEffect(() => { void ladeStand(); }, [ladeStand]);

  // Der Zaehler des Erfolgsschritts. Laeuft er ab, geht es weiter; ein Klick auf
  // „Abbrechen" setzt ihn auf `null` und die Seite bleibt stehen.
  useEffect(() => {
    if (restSekunden === null) return;
    if (restSekunden <= 0) { onFertig?.(); return; }
    const t = setTimeout(() => setRestSekunden((n) => (n === null ? null : n - 1)), 1000);
    return () => clearTimeout(t);
  }, [restSekunden, onFertig]);

  // Pflicht-Einrichtung: nicht erst einen Knopf anbieten. Ein „Einrichten"-Knopf auf
  // einer Seite, die man ohne Einrichtung nicht verlassen kann, ist eine Scheinfrage.
  //
  // Der Ref sorgt fuer GENAU EINEN Anlauf je Seitenaufruf. Ohne ihn kann der Effekt
  // ein zweites Mal laufen (React spielt Effekte in der Entwicklung doppelt aus, und
  // `faktoren` aendert sich nach dem Laden), und der zweite Anlauf laeuft in
  // „A factor with the friendly name … already exists".
  const anlaufGestartet = useRef(false);
  useEffect(() => {
    if (!erzwungen || faktoren === null || aktiv || enroll || schritt !== "uebersicht") return;
    if (anlaufGestartet.current) return;
    anlaufGestartet.current = true;
    void starteEinrichtung();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [erzwungen, faktoren, aktiv, enroll, schritt]);

  /**
   * **Vor dem Anlegen die Leichen wegraeumen.**
   *
   * `enroll()` bricht mit „A factor with the friendly name „Janeway" for this user
   * already exists" ab, sobald ein Faktor dieses Namens existiert — auch ein
   * `unverified`. Und genau so einer bleibt bei JEDEM abgebrochenen Versuch liegen:
   * Seite neu geladen, Tab geschlossen, Netz weg. Beim naechsten Aufruf war die
   * Einrichtung damit dauerhaft blockiert — auf einer Seite, die man ohne sie nicht
   * verlassen kann. Am 2026-08-19 im ersten echten Durchlauf passiert.
   *
   * Den vorhandenen Faktor WEITERZUBENUTZEN geht nicht: `enroll()` gibt das Geheimnis
   * genau einmal heraus, und ohne das gibt es keinen QR-Code mehr. Er muss weg.
   *
   * Nur `unverified` — ein verifizierter Faktor wird hier NIE angefasst. Das waere
   * ein stiller Rechteentzug: die Seite kommt nur hierher, wenn gar keiner gilt.
   */
  async function raeumeHalbeFaktorenWeg(client: MfaClient): Promise<void> {
    const { data } = await client.auth.mfa.listFactors();
    // **`data.all`, NICHT `data.totp`.** `listFactors()` schiebt einen Faktor nur dann
    // in `totp`, wenn er `verified` ist (`GoTrueClient._listFactors`) — die halbfertigen
    // stehen ausschliesslich in `all`. Ueber `totp` zu laufen heisst also, genau den Fall
    // auszulassen, um den es hier geht: das Aufraeumen lief ins Leere, und die Seite
    // blieb blockiert. Am 2026-08-19 zweimal hintereinander so gebaut.
    for (const f of data?.all ?? []) {
      if (f.status !== "verified" && (f.factor_type ?? "totp") === "totp") {
        await client.auth.mfa.unenroll({ factorId: f.id });
      }
    }
  }

  async function starteEinrichtung() {
    setFehler(null); setLaeuft(true);
    const client = await mfaClient();
    await raeumeHalbeFaktorenWeg(client);
    const { data, error } = await client.auth.mfa.enroll({ factorType: "totp", friendlyName: "Janeway" });
    setLaeuft(false);
    if (error || !data) {
      // `laeuft` ist hier schon false — sonst dreht sich der Ladekreis weiter, waehrend
      // darunter ein Fehler steht. Genau dieses Bild gab es am 2026-08-19.
      setFehler(error?.message ?? "Einrichtung fehlgeschlagen.");
      return;
    }
    setEnroll({ id: data.id, uri: data.totp?.uri, secret: data.totp?.secret });
    setSchritt("einrichten");
  }

  async function pruefeCode() {
    if (!enroll) return;
    setFehler(null); setLaeuft(true);
    const client = await mfaClient();
    const { data: ch, error: chFehler } = await client.auth.mfa.challenge({ factorId: enroll.id });
    if (chFehler || !ch) { setLaeuft(false); setFehler(chFehler?.message ?? "Prüfung fehlgeschlagen."); return; }
    const { error: vFehler } = await client.auth.mfa.verify({ factorId: enroll.id, challengeId: ch.id, code });
    if (vFehler) { setLaeuft(false); setFehler(vFehler.message); return; }

    // Faktor steht. Jetzt der Schluessel — die Sitzung ist in diesem Moment `aal2`, und
    // genau das verlangt der Server fuer eine Neuausstellung.
    const res = await fetch(`${api}?aktion=erzeugen`, { method: "POST" });
    setLaeuft(false);
    const j = (await res.json().catch(() => ({}))) as {
      data?: { schluessel: string; datei: string }; error?: string;
    };
    if (!res.ok || !j.data) { setFehler(j.error ?? "Der Wiederherstellungsschlüssel konnte nicht erzeugt werden."); return; }
    setNeuerSchluessel(j.data.schluessel); setDatei(j.data.datei);
    setCode(""); setSchritt("codes");
    void ladeStand();
  }

  /**
   * Schluessel nachtraeglich erzeugen — fuer alle, die schon einen Faktor haben.
   *
   * Nach dem Umstieg auf EINEN Schluessel (Migration 520) steht bei ihnen keiner mehr:
   * die alten zehn Codes sind weg. Sie muessen deshalb NICHT den Faktor neu einrichten,
   * nur den Schluessel holen — und das geht nur bei `aal2`, also in einer Sitzung, die
   * den zweiten Faktor gerade nachgewiesen hat. Genau das ist hier der Fall, sonst waere
   * der Reiter nicht erreichbar.
   */
  async function erzeugeNeuenSchluessel() {
    setFehler(null); setLaeuft(true);
    const res = await fetch(`${api}?aktion=erzeugen`, { method: "POST" });
    setLaeuft(false);
    const j = (await res.json().catch(() => ({}))) as {
      data?: { schluessel: string; datei: string }; error?: string;
    };
    if (!res.ok || !j.data) { setFehler(j.error ?? "Der Schlüssel konnte nicht erzeugt werden."); return; }
    setNeuerSchluessel(j.data.schluessel); setDatei(j.data.datei); setSchritt("codes");
    void ladeStand();
  }

  async function bestaetigeCode() {
    setFehler(null); setLaeuft(true);
    const res = await fetch(`${api}?aktion=bestaetigen`, {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({ schluessel: bestaetigung }),
    });
    setLaeuft(false);
    const j = (await res.json().catch(() => ({}))) as { error?: string };
    if (!res.ok) { setFehler(j.error ?? "Bestätigung fehlgeschlagen."); return; }
    // Erst JETZT verschwindet der Schluessel vom Schirm. Ihn vorher wegzunehmen waere
    // die bequemere Reihenfolge und genau die, bei der jemand ohne ihn dasteht.
    setNeuerSchluessel(null); setBestaetigung("");
    void ladeStand();
    // **Nicht sofort weiterspringen.** Der erste Durchlauf endete damit, dass nach der
    // letzten Eingabe „nichts passierte" — der Sprung kam ohne ein Wort dazu, und wer
    // vorher eine rote Meldung gesehen hatte, konnte nicht wissen, ob es geklappt hat.
    // Der Erfolg bekommt jetzt einen eigenen Schritt, mit Knopf UND Zaehler.
    // Aus der Uebersicht heraus (Nachholen) gibt es kein Ziel, auf das es weiterginge —
    // dann zurueck in die Uebersicht, wo die Warnung nun verschwindet.
    setSchritt(erzwungen ? "fertig" : "uebersicht");
    if (erzwungen) setRestSekunden(6);
    if (!erzwungen) onFertig?.();
  }

  async function entferne(faktorId: string) {
    setFehler(null); setLaeuft(true);
    const { error } = await (await mfaClient()).auth.mfa.unenroll({ factorId: faktorId });
    setLaeuft(false);
    if (error) { setFehler(error.message); return; }
    void ladeStand();
  }

  function herunterladen() {
    const blob = new Blob([datei], { type: "text/plain;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "janeway-wiederherstellungsschluessel.txt";
    a.click();
    URL.revokeObjectURL(a.href);
  }

  async function kopieren() {
    await navigator.clipboard.writeText(neuerSchluessel ?? "");
    setKopiert(true);
    setTimeout(() => setKopiert(false), 2000);
  }

  const karte = "rounded-[calc(var(--radius)+4px)] border border-border bg-card p-6";
  const huelle = breit ? "space-y-4" : "max-w-xl space-y-4";

  if (stand === "fehlt") {
    return (
      <div className={huelle}>
        <div className={`${karte} border-destructive`}>
          <div className="flex gap-2 items-start">
            <AlertTriangle size={16} className="text-destructive shrink-0 mt-0.5" />
            <div className="space-y-1">
              <div className="text-sm font-medium text-destructive">Diese App kann die Zwei-Faktor-Anmeldung nicht verwalten.</div>
              <p className="text-xs text-muted-foreground">
                Ihr fehlt die Route <code>/api/v1/zweitfaktor</code>. Das ist ein Fehler in der
                App, keine Einstellung; bitte im Anmeldedienst einrichten.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={huelle}>
      {/* Der Werbesatz „Ein zweiter Faktor schuetzt dein Konto …" stand hier bis
          0.339.0 als freier Absatz VOR der Karte. Im Sicherheits-Reiter schwebte
          er damit zwischen Kennzahlenreihe und Karte, ausserhalb jeder Flaeche —
          und er sagte, was die Kennzahl darueber ohnehin sagt. Ersatzlos weg:
          wer den Reiter „Sicherheit" oeffnet, muss nicht ueberzeugt werden. */}

      {erzwungen && schritt !== "uebersicht" && (
        <div className="pb-1">
          <Fortschritt aktiv={schritt} />
        </div>
      )}

      {fehler && <p className="text-xs text-destructive">{fehler}</p>}

      {/* Faktor steht schon, die Seite wurde aber trotzdem aufgerufen (Lesezeichen,
          zurueck-Taste). Ohne diesen Zweig stuende hier nur die Ueberschrift und
          „Abmelden" — eine Sackgasse. */}
      {erzwungen && schritt === "uebersicht" && aktiv && (
        <div className={`${karte} space-y-4`}>
          <div className="flex items-start gap-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-foreground/40 bg-foreground/10">
              <Check size={16} strokeWidth={2.5} />
            </span>
            <div className="space-y-1">
              <div className="text-sm font-medium">Zwei-Faktor-Anmeldung ist bereits aktiv</div>
              <p className="text-xs text-muted-foreground">
                Für dieses Konto ist hier nichts mehr zu tun.
              </p>
            </div>
          </div>
          <Button size="sm" onClick={() => onFertig?.()} aria-label="Weiter">
            {zielName ? `Weiter zu ${zielName}` : "Weiter"}
          </Button>
        </div>
      )}

      {erzwungen && schritt === "uebersicht" && !aktiv && !fehler && (
        <div className={`${karte} flex items-center gap-3`}>
          <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-foreground/20 border-t-foreground" />
          <span className="text-xs text-muted-foreground">Einrichtung wird vorbereitet …</span>
        </div>
      )}

      {/* Scheitert der Anlauf, MUSS es hier weitergehen koennen: die Seite laesst sich
          ohne abgeschlossene Einrichtung nicht verlassen. Ohne diesen Knopf steht man
          vor einer Fehlermeldung und einem sich drehenden Kreis. */}
      {erzwungen && schritt === "uebersicht" && !aktiv && fehler && (
        <div className={`${karte} space-y-3`}>
          <p className="text-xs text-muted-foreground">
            Die Einrichtung konnte nicht vorbereitet werden.
          </p>
          <Button
            size="sm"
            disabled={laeuft}
            onClick={() => { anlaufGestartet.current = true; void starteEinrichtung(); }}
            aria-label="Einrichtung erneut versuchen"
          >
            {laeuft ? "…" : "Erneut versuchen"}
          </Button>
        </div>
      )}

      {!erzwungen && schritt === "uebersicht" && (
        <div className={karte}>
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-1">
              <div className="text-sm font-medium flex items-center gap-2">
                <ShieldCheck size={15} />
                {aktiv ? "Zwei-Faktor-Anmeldung ist aktiv" : "Zwei-Faktor-Anmeldung ist aus"}
              </div>
              <p className="text-[11px] text-muted-foreground">
                {aktiv
                  ? "Bei jeder Anmeldung wird zusätzlich ein Code aus deiner Authenticator-App verlangt."
                  : "Du meldest dich nur mit E-Mail und Passwort an."}
              </p>
            </div>
            {aktiv ? (
              <Button
                variant="outline" size="sm" disabled={laeuft}
                aria-label="Zwei-Faktor-Anmeldung entfernen"
                onClick={() => { const f = (faktoren ?? []).find((x) => x.status === "verified"); if (f) void entferne(f.id); }}
              >
                Entfernen
              </Button>
            ) : (
              <Button size="sm" disabled={laeuft} onClick={starteEinrichtung} aria-label="Zwei-Faktor-Anmeldung einrichten">
                Einrichten
              </Button>
            )}
          </div>

          {aktiv && stand && (
            <div className="mt-4 pt-4 border-t border-border space-y-2">
              {/* Der Hinweis MIT Weg — bestaetigen liess sich der Schluessel bis
                  2026-08-19 nur unmittelbar nach dem Erzeugen; wer den Schritt einmal
                  verpasst hatte, las die Warnung fuer immer.

                  **Kein Nachhol-Knopf fuer die Bestaetigung mehr, sondern ein neuer
                  Schluessel.** Den alten kennt niemand mehr — er stand genau einmal auf
                  dem Schirm, und der Server haelt nur den Hash. Einen zu bestaetigen,
                  den man nicht hat, ist keine Handlung, die man anbieten kann. */}
              {!stand.bestaetigt_am && (
                <div className="space-y-2">
                  <div className="flex items-start gap-2 text-xs text-destructive">
                    <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                    <span>
                      {stand.vorhanden
                        ? "Dein Wiederherstellungsschlüssel ist nicht bestätigt. Ohne ihn ist ein verlorenes Telefon ein verlorenes Konto."
                        : "Du hast keinen Wiederherstellungsschlüssel. Ohne ihn ist ein verlorenes Telefon ein verlorenes Konto."}
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={laeuft}
                    onClick={() => void erzeugeNeuenSchluessel()}
                    aria-label="Neuen Wiederherstellungsschlüssel erzeugen"
                  >
                    {laeuft ? "…" : "Schlüssel jetzt erzeugen"}
                  </Button>
                </div>
              )}
              <div className="text-[11px] text-muted-foreground">
                Wiederherstellungsschlüssel:{" "}
                {stand.bestaetigt_am ? "vorhanden und bestätigt" : stand.vorhanden ? "erzeugt, nicht bestätigt" : "keiner"}
              </div>
            </div>
          )}
        </div>
      )}

      {schritt === "einrichten" && enroll && (
        <div className={`${karte} space-y-4`}>
          <div className="text-sm font-medium">QR-Code mit der Authenticator-App scannen</div>
          {enroll.uri && (
            <div className="flex justify-center">
              <div className="rounded-2xl border border-border bg-card p-3 shadow-sm">
                <div className="rounded-xl bg-white p-3">
                  <QRCodeSVG
                    value={enroll.uri}
                    size={168}
                    level="H"
                    marginSize={0}
                    fgColor={QR_VORDERGRUND}
                    bgColor={QR_HINTERGRUND}
                    title="QR-Code für die Authenticator-App"
                    imageSettings={{ src: QR_GLYPH, height: 34, width: 34, excavate: true }}
                  />
                </div>
              </div>
            </div>
          )}
          {enroll.secret && (
            <div className="space-y-1">
              <div className="text-[11px] text-muted-foreground">Oder von Hand eingeben:</div>
              <code className="text-xs break-all select-all">{enroll.secret}</code>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="zf-code">Sechsstelligen Code aus der App eingeben</Label>
            <Input
              id="zf-code" value={code} inputMode="numeric" autoComplete="one-time-code"
              placeholder="123456" maxLength={6}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            />
          </div>
          <div className="flex gap-2">
            <Button size="sm" disabled={laeuft || code.length < 6} onClick={pruefeCode} aria-label="Code prüfen">
              {laeuft ? "…" : "Weiter"}
            </Button>
            {!erzwungen && (
              <Button variant="outline" size="sm" disabled={laeuft} onClick={() => { setSchritt("uebersicht"); setEnroll(null); }} aria-label="Einrichtung abbrechen">
                Abbrechen
              </Button>
            )}
          </div>
        </div>
      )}

      {schritt === "codes" && neuerSchluessel && (
        <div className={`${karte} space-y-4`}>
          <div className="space-y-1">
            <div className="text-sm font-medium">Wiederherstellungsschlüssel sichern</div>
            <p className="text-xs text-muted-foreground">
              Er funktioniert genau einmal und <strong>meldet dich nicht an</strong>: er
              entfernt die Zwei-Faktor-Anmeldung, damit du wieder hereinkommst, wenn dein
              Telefon weg ist. Bewahre ihn so sicher auf wie dein Passwort. Du siehst ihn
              nur dieses eine Mal.
            </p>
          </div>
          {/* Gross, umbrechbar, auswaehlbar: er wird abgeschrieben oder kopiert, nicht
              ueberflogen. `select-all` spart das muehsame Markieren mit der Maus. */}
          <div className="select-all break-all rounded-[var(--radius)] border border-border bg-muted/40 px-3 py-3 text-center font-mono text-sm tracking-wide">
            {neuerSchluessel}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={herunterladen} aria-label="Schlüssel herunterladen">
              <Download size={14} /> Herunterladen
            </Button>
            <Button variant="outline" size="sm" onClick={kopieren} aria-label="Schlüssel kopieren">
              {kopiert ? <Check size={14} /> : <Copy size={14} />} {kopiert ? "Kopiert" : "Kopieren"}
            </Button>
            <Button size="sm" onClick={() => setSchritt("bestaetigen")} aria-label="Weiter zur Bestätigung">
              Weiter
            </Button>
          </div>
        </div>
      )}

      {schritt === "fertig" && (
        <div className={`${karte} space-y-4`}>
          <div className="flex items-start gap-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-foreground/40 bg-foreground/10">
              <Check size={16} strokeWidth={2.5} />
            </span>
            <div className="space-y-1">
              <div className="text-sm font-medium">Zwei-Faktor-Anmeldung ist aktiv</div>
              <p className="text-xs text-muted-foreground">
                Ab der nächsten Anmeldung fragen wir zusätzlich den Code aus deiner
                Authenticator-App. Dein Wiederherstellungsschlüssel ist bestätigt. Bewahre
                ihn so sicher auf wie dein Passwort.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={() => onFertig?.()} aria-label="Weiter">
              {zielName ? `Weiter zu ${zielName}` : "Weiter"}
            </Button>
            {restSekunden !== null && (
              <>
                <span className="text-[11px] text-muted-foreground tabular-nums">
                  Weiter in {restSekunden}&nbsp;s
                </span>
                <button
                  type="button"
                  onClick={() => setRestSekunden(null)}
                  className="text-[11px] text-muted-foreground underline underline-offset-2"
                >
                  Hier bleiben
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {schritt === "bestaetigen" && (
        <div className={`${karte} space-y-4`}>
          <div className="space-y-1">
            <div className="text-sm font-medium">Bestätigen, dass du den Schlüssel hast</div>
            <p className="text-xs text-muted-foreground">
              Gib ihn <strong>vollständig</strong> ein. Das ist der Beleg, dass er wirklich
              gespeichert ist; er wird dabei nicht verbraucht. Bindestriche und
              Groß-/Kleinschreibung sind egal.
            </p>
          </div>
          <Input
            value={bestaetigung} autoComplete="off" placeholder="XXXX-XXXX-XXXX-XXXX-XXXX-XXXX"
            aria-label="Wiederherstellungsschlüssel"
            className="font-mono"
            onChange={(e) => setBestaetigung(e.target.value.toUpperCase())}
          />
          <div className="flex gap-2">
            <Button size="sm" disabled={laeuft || !bestaetigung} onClick={bestaetigeCode} aria-label="Bestätigung senden">
              {laeuft ? "…" : "Bestätigen"}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setSchritt("codes")} aria-label="Zurück zum Schlüssel">
              Zurück zum Schlüssel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

